// @ts-nocheck
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchIreadyScores, saveIreadyScore } from '../services/api';

const WINDOW_ORDER = ['fall', 'winter', 'spring'];
const WINDOW_STYLES = {
  fall: { label: 'Fall', bg: '#dbeafe', border: '#3b82f6', text: '#1e3a8a' },
  winter: { label: 'Winter', bg: '#f1f5f9', border: '#94a3b8', text: '#334155' },
  spring: { label: 'Spring', bg: '#f1f5f9', border: '#94a3b8', text: '#334155' },
};
const SUBJECT_LABELS = { reading: 'Literacy', math: 'Math' };

function ordinal(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

function InfoTooltip() {
  const [open, setOpen] = useState<any>(false);
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', marginLeft: 6 }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span
        role="img"
        aria-label="What is an i-Ready score?"
        style={{
          width: 16, height: 16, borderRadius: '50%', background: '#94a3b8', color: '#fff',
          fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'help', lineHeight: 1,
        }}
      >
        ?
      </span>
      {open && (
        <span
          style={{
            position: 'absolute', top: '130%', left: '50%', transform: 'translateX(-50%)',
            width: 260, background: '#1e293b', color: '#f8fafc', fontSize: 12, lineHeight: 1.5,
            borderRadius: 8, padding: '10px 12px', boxShadow: '0 8px 20px rgba(0,0,0,0.25)', zIndex: 20,
            fontWeight: 400, textAlign: 'left',
          }}
        >
          i-Ready is an adaptive diagnostic test given three times a year (Fall, Winter, Spring) in Literacy and Math.
          The score shown is your child's national percentile rank compared to other students in the same grade —
          for example, "60th percentile" means they scored as well as or better than 60% of students nationally.
        </span>
      )}
    </span>
  );
}

/**
 * Displays a student's i-Ready Fall/Winter/Spring percentile scores (Literacy + Math only)
 * as a color-coded grid; missing scores render as blank boxes. Pass `editable` to allow
 * inline editing (the backend still enforces per-subject teacher permissions).
 */
export default function IreadyScoreBoxes({ studentId, editable = false, showInfoTooltip = false, title = 'i-Ready Scores', subjects = ['reading', 'math'] }: any) {
  const [grid, setGrid] = useState<any>(null);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');
  const [editingKey, setEditingKey] = useState<any>('');
  const [draftValue, setDraftValue] = useState<any>('');
  const [saving, setSaving] = useState<any>(false);
  const [saveError, setSaveError] = useState<any>('');
  const committingRef = useRef(false);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchIreadyScores(studentId);
      setGrid(data);
    } catch (e: any) {
      setError(e.message || 'Could not load i-Ready scores.');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  if (!studentId) return null;
  if (loading) return <div style={{ fontSize: 13, color: '#64748b' }}>Loading i-Ready scores…</div>;
  // No access (or load failure) hides the panel instead of showing an error.
  if (error) return null;
  if (!grid) return null;

  const startEdit = (window, subject, currentScore) => {
    setSaveError('');
    setEditingKey(`${window}:${subject}`);
    setDraftValue(currentScore ?? '');
  };

  const cancelEdit = () => {
    setEditingKey('');
    setDraftValue('');
    setSaveError('');
  };

  const commitEdit = async (window, subject) => {
    if (committingRef.current) return;
    const trimmed = String(draftValue).trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    if (parsed !== null && (!Number.isInteger(parsed) || parsed < 0 || parsed > 99)) {
      setSaveError('Enter a percentile between 0 and 99.');
      return;
    }
    const current = (grid.windows[window] || {})[subject] ?? null;
    if (parsed === current) {
      cancelEdit();
      return;
    }
    committingRef.current = true;
    setSaving(true);
    setSaveError('');
    try {
      const updated = await saveIreadyScore(studentId, { subject, window, score: parsed, school_year: grid.school_year });
      setGrid(updated);
      setEditingKey('');
      setDraftValue('');
    } catch (e: any) {
      setSaveError(e.message || 'Failed to save score.');
    } finally {
      committingRef.current = false;
      setSaving(false);
    }
  };

  const renderCell = (window, subject) => {
    const style = WINDOW_STYLES[window];
    const score = (grid.windows[window] || {})[subject];
    const key = `${window}:${subject}`;
    const hasScore = score !== null && score !== undefined;

    if (editingKey === key) {
      return (
        <div key={key} style={{ ...cellBase, background: style.bg, borderColor: style.border }}>
          <input
            type="number"
            min={0}
            max={99}
            autoFocus
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitEdit(window, subject);
              if (e.key === 'Escape') cancelEdit();
            }}
            onBlur={() => commitEdit(window, subject)}
            placeholder="0-99"
            disabled={saving}
            style={{ width: '100%', maxWidth: 64, padding: '3px 6px', border: `1px solid ${style.border}`, borderRadius: 6, fontSize: 13, textAlign: 'center', opacity: saving ? 0.6 : 1 }}
          />
        </div>
      );
    }

    return (
      <div
        key={key}
        onClick={editable ? () => startEdit(window, subject, score) : undefined}
        title={editable ? 'Click to enter or edit' : undefined}
        style={{
          ...cellBase,
          background: hasScore ? style.bg : '#fff',
          borderColor: hasScore ? style.border : '#e2e8f0',
          borderStyle: hasScore ? 'solid' : 'dashed',
          cursor: editable ? 'pointer' : 'default',
        }}
      >
        {hasScore && (
          <>
            <span style={{ fontSize: 16, fontWeight: 800, color: style.text, lineHeight: 1 }}>{ordinal(score)}</span>
            <span style={{ fontSize: 10, color: style.text, opacity: 0.8, marginTop: 2 }}>percentile</span>
          </>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 800, color: '#64748b', marginBottom: 8 }}>
        {title}
        {showInfoTooltip && <InfoTooltip />}
        <span style={{ marginLeft: 8, fontWeight: 600, textTransform: 'none', letterSpacing: 0, color: '#64748b' }}>{grid.school_year}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '56px repeat(3, minmax(0, 1fr))', gap: 6, alignItems: 'stretch' }}>
        <div />
        {WINDOW_ORDER.map((window) => {
          const style = WINDOW_STYLES[window];
          return (
            <div
              key={`head-${window}`}
              style={{ textAlign: 'center', fontSize: 11, fontWeight: 800, color: style.text, background: style.bg, border: `1px solid ${style.border}`, borderRadius: 8, padding: '4px 0' }}
            >
              {style.label}
            </div>
          );
        })}
        {subjects.map((subject) => (
          <React.Fragment key={subject}>
            <div style={{ display: 'flex', alignItems: 'center', fontSize: 12, fontWeight: 700, color: '#334155' }}>
              {SUBJECT_LABELS[subject]}
            </div>
            {WINDOW_ORDER.map((window) => renderCell(window, subject))}
          </React.Fragment>
        ))}
      </div>
      {editable && (
        <div style={{ fontSize: 10, color: '#64748b', marginTop: 6 }}>Click a box to enter a percentile (0-99).</div>
      )}
      {saveError && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 6 }}>{saveError}</div>}
    </div>
  );
}

const cellBase = {
  minHeight: 44,
  borderRadius: 8,
  borderWidth: 1,
  borderStyle: 'solid',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px 2px',
};
